import { LANGUAGES, useLang, useT, type Lang } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { Select } from '@/components/Select'

/**
 * Language picker. `compact`: a small select for the desktop nav and the login screen, showing
 * the short code (EN / DE / CG / BS) but the full names in its list. `list`: one row per
 * language, for the phone drawer, where a 44px target per option beats a native select.
 *
 * Each language is labelled in itself (Deutsch, Crnogorski …): someone who can't read the
 * current language must still be able to find their own.
 */
export function LanguageSwitcher({ variant = 'compact', tone = 'light' }: { variant?: 'compact' | 'list'; tone?: 'light' | 'dark' }) {
  const { lang, setLang } = useLang()
  const t = useT(commonMessages)
  const current = LANGUAGES.find((l) => l.code === lang)

  if (variant === 'list') {
    return (
      <div role="radiogroup" aria-label={t('language')} className="grid grid-cols-2 gap-1.5">
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            type="button"
            role="radio"
            aria-checked={l.code === lang}
            lang={l.code}
            onClick={() => setLang(l.code)}
            className={`min-h-11 rounded-xl px-3 text-left text-sm font-medium transition ${
              l.code === lang
                ? 'bg-brand-600 text-white'
                : 'text-neutral-700 ring-1 ring-neutral-300 hover:bg-black/5 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-white/10'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
    )
  }

  return (
    <Select
      value={lang}
      onChange={(v) => setLang(v as Lang)}
      options={LANGUAGES.map((l) => ({ value: l.code, label: l.label }))}
      {...(current?.short ? { triggerLabel: current.short } : {})}
      aria-label={t('language')}
      size="sm"
      tone={tone}
      className="w-fit"
      triggerClassName="rounded-full"
      prefix={
        <svg viewBox="0 0 24 24" className={`h-4 w-4 shrink-0 ${tone === 'dark' ? 'text-white/60' : 'text-neutral-400'}`} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
      }
    />
  )
}
